import { useEffect, useRef, useState } from 'react';
import { ym } from 'react-metrika';

import { MainConfig } from '@/config/MainConfig.ts';
import { isProd } from '@/env.ts';
import { isAndroidApp } from '@/utils';

export function useTimer(initialTime = 0, interval = 1000) {
    const [time, setTime] = useState(initialTime);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const start = () => {
        if (!timerRef.current) {
            timerRef.current = setInterval(() => {
                setTime(prevTime => {
                    if (prevTime <= 1 && timerRef.current) {
                        clearInterval(timerRef.current);
                        timerRef.current = null;
                        return 0;
                    }
                    return prevTime - 1;
                });
            }, interval);
        }
    };

    const stop = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    };

    const reset = () => {
        stop();
        setTime(initialTime);
    };

    useEffect(() => {
        setTime(initialTime);
    }, [initialTime]);

    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, []);

    return { time, start, stop, reset };
}

export const useFirstRender = () => {
    const isFirstRender = useRef(true);

    if (isFirstRender.current) {
        isFirstRender.current = false;
        return true;
    }

    return false;
};

/**
 * Хук для прослушивания кастомного события `androidApp:back`
 * @param callback Функция, вызываемая при срабатывании события
 */
export const useAndroidBackEvent = (callback: (event: Event) => void): void => {
    useEffect(() => {
        if (!isAndroidApp()) {
            return;
        }

        const handleBackEvent = (event: Event) => {
            if (callback) {
                callback(event);
            }
        };

        document.addEventListener('androidApp:back', handleBackEvent);

        return () => {
            document.removeEventListener('androidApp:back', handleBackEvent);
        };
    }, [callback]);
};

/**
 * Отправляет событие в яндекс метрику
 * при монтировании компонента
 *
 * @param goal
 * @param params
 */
export const useYMOnMount = (goal: string, params?: Record<string, string>) => {
    useEffect(() => {
        if (isProd()) {
            ym(MainConfig.metric.yandex.id, 'reachGoal', goal, params);
        }
    }, [goal, params]);
};
